/* eslint-disable no-unsafe-optional-chaining */
import TvGuideService from "core/services/tv-guide.service";
import context from "core/tv-guide.context";
import { SearchRequest, ChannelWithMultipleDaysProgrammes, ChannelWithProgrammes, ChannelsWithProgrammes, SearchCriteria, GenreWithValue, ScheduleDictionary, ChannelDictionary } from "core/types";
import TvGuideStore from "./tv-guide.store";
import { createDateRange } from "core/lib/time-methods";
import NewsService from "core/services/news.service";
import dateWrapper from "core/lib/date-wrapper";

const dictionary = context.dictionary;

const service = new TvGuideService();

export const getProgrammesForDates = async (
    dates: Date[],
    requestDetails: SearchCriteria
) => {
    const tasks = [];

    const genresToIgnore = [dictionary.allGenres, "All Genres", undefined];

    let { genre, subGenre } = requestDetails;

    const $package = requestDetails.package ?? "";
    genre = genresToIgnore.includes(genre) ? "" : genre;
    subGenre = subGenre ?? "";

    for (let i = 0; i < dates.length; i++) {
        const date = dateWrapper(dates[i]).yearMonthDay();

        const task = service.getProgrammes(date, $package, genre, subGenre);

        tasks.push(task);
    }

    return Promise.all(tasks); //Need Promise.all?
}

export const buildAndParseData = async (request: SearchRequest) => {
    const {
        programmeSchedule,
        channelNumbers,
        dates,
        criteria
    } = request;

    const responses = await getProgrammesForDates(dates, criteria);

    const responseData = responses.map(x => x.data);

    const clonedProgrammeSchedule = JSON.parse(JSON.stringify(programmeSchedule));

    // eslint-disable-next-line prefer-const
    const result = mergeData(
        responseData,
        channelNumbers,
        clonedProgrammeSchedule
    );

    return result;
}

/**
 * Build the programme-channel array and channel list.
 * - If the genre or package has changed, clear any current data.
 * - If the date has changed, only add the additional data (reduce duplicates and double fetches)
 */
const mergeData = (
    programmeData: ChannelsWithProgrammes[],
    channelList: number[],
    currentSchedule: ScheduleDictionary
) => {
    // TODO: consider using a keyed array for the days instead of this messed up anon array...
    const defaultChannels: number[] = [];

    let channelNumbers = new Set(channelList ?? defaultChannels);

    if (!programmeData?.length) {
        return {
            schedule: currentSchedule,
            channels: Array.from(channelNumbers)
        };
    }

    let schedule = currentSchedule;

    // Check if the results length equals the additional required item length
    for (let i = 0; i < programmeData.length; i++) {
        let channelList = programmeData[i].channels;

        if (context.channelTags) {
            channelList = channelList?.filter((channel) => context.channelTags.includes(channel.tag));
        }

        const channelData = buildScheduleAndChannelList(channelList);

        const newChannelData = channelData.channels;
        const channelNumberList = channelData.channelList;

        if (!channelList === undefined) {
            schedule = newChannelData;
            channelNumbers = new Set(channelNumberList);
            continue;
        }

        // NOTE: Use set to ensure all new channels are added to context without duplicates
        channelNumbers = new Set([...channelNumbers, ...channelNumberList].sort());

        // eslint-disable-next-line no-loop-func
        channelNumbers.forEach((channelNumber: number) => {
            const p = schedule[channelNumber];
            const c = newChannelData[channelNumber];

            if (p?.programmes && c?.programmes) {
                c.dates.forEach(d => {
                    p.programmes[d] = c.programmes[d];
                    if (!p.dates.includes(d)) {
                        p.dates.push(d);
                    }
                });

                return;
            }

            if (c) {
                // NOTE: If doesn't exist in context but is returned from API (add new channel with filler array for prev columns)
                schedule[channelNumber] = c;
                return;
            }
        });
    }

    const channels = Array.from(channelNumbers);
    return {
        schedule,
        channels
    };
}

/**
 * Create list of channels and convert programmes from a
 * 1d Array to a 2d array so that programme arrs can be
 * grouped under one property but for different days
 * i.e [todays programmes, tomorrows programmes, etc]
 *  */
//data = ChannelWithProgrammes[]
export const buildScheduleAndChannelList = (data: ChannelWithProgrammes[]) => {
    const schedule: ScheduleDictionary = {};
    let channelList: number[] = [];

    data?.forEach((x) => {
        const channelNumber = parseInt(x.number);
        channelList.push(channelNumber);

        const date = x.programmes[0].endTime.substring(0, 10);

        const channel: ChannelWithMultipleDaysProgrammes = {
            number: channelNumber,
            name: x.name,
            tag: x.tag,
            programmes: {},
            dates: [date]
        };

        channel.programmes[date] = x.programmes;

        schedule[channelNumber] = channel;
    });

    channelList = channelList.sort();

    return { channels: schedule, channelList };
}

const _maxRetries = 3;

class DataManager {
    private _store: TvGuideStore;
    private _isPopulating = false;
    private _retries = 0;

    constructor(store: TvGuideStore) {
        this._store = store;
    }

    private _incrementRetries() {
        if (this._retries === _maxRetries) {
            throw new Error("Reached maximum retry limit");
        }

        this._retries++;
    }

    private _resetRetries() {
        this._retries = 0;
    }

    get store() {
        return this._store;
    }

    async populate() {
        if (this._isPopulating) {
            return;
        }

        this._isPopulating = true;

        const dates = createDateRange();

        this._store.updateDates(dates);

        const genresTask = service.getGenres();
        const packagesTask = service.getPackages();
        const channelsTask = service.getChannels();

        const genresResponse = await genresTask;
        const genres = genresResponse.data as GenreWithValue[];

        if (!genres) {
            throw new Error("Error getting genres");
        }

        if (context.fifaWorldCup) {
            const wcTitle = "FIFA World Cup";
            const genre: GenreWithValue = {
                mainGenre: wcTitle,
                subGenres: [],
                value: "Sport",
                subGenre: wcTitle
            }

            genres.unshift(genre);
        }

        {
            const genre: GenreWithValue = {
                mainGenre: context.dictionary.allGenres,
                subGenres: [],
                value: ""
            }

            genres.unshift(genre);
        }

        this.store.updateGenres(genres);

        const packagesResponse = await packagesTask;

        const firstPackage = packagesResponse.data[0];

        const packagesToIgnore = ["Decoder Payment Plan", "Portuguese + Indian"];
        const filteredPackages = packagesResponse.data.filter((label: string) => {
            return !packagesToIgnore.includes(label);
        });

        this._store.updatePackages(filteredPackages);

        this._store.updateHasBasicData(true);

        this._store.updateSearchCriteria({
            package: firstPackage
        });

        const datesToFetch: Date[] = [dates[0]]; //Just get first date;

        await this.populateSchedule(datesToFetch, false);

        //Get News articles
        if (context.showNewsArticles) {
            const newsResponse = await new NewsService().getNewsArticles(firstPackage);
            this._store.updateNewsArticles(newsResponse.data.items);
        }

        //Get channels based on channelTags or full list

        const response = await channelsTask;

        try {
            const channels: ChannelDictionary = {};
            response.data.forEach(x => channels[x.number] = x);

            this._store.updateChannels(channels);
        } catch (error) {
            console.log("Error getting channels");
            this._isPopulating = false;
            this._incrementRetries();
            await this.populate();
            return;
        }

        this._store.updateHasScheduleData(true);

        this._isPopulating = false;
    }

    private async _populate(request: SearchRequest, updateIsPopulating = true) {
        this.store.updateIsFetching(true);

        if (updateIsPopulating) {
            this._isPopulating = true;
        }

        request.dates = request.dates ?? [this._store.dates[0]];

        const { schedule, channels } = await buildAndParseData(request);

        const currentDates = this._store.scheduleDates;

        const additionalDates = request.dates.filter(x => !this._store.scheduleDates.includes(x))

        additionalDates.forEach(x => currentDates.push(x));

        this._store.updateSearchCriteria(request.criteria);
        this._store.updateScheduleDates(currentDates); //TODO: Make computed
        this._store.updateChannelNumbers(channels);
        this._store.updateSchedule(schedule);

        this._store.updateIsFetching(false);

        this._resetRetries();

        if (updateIsPopulating) {
            this._isPopulating = false;
        }
    }

    async populateSchedule(dates: Date[], updateIsPopulating = true) {
        const request: SearchRequest = {
            dates: dates,
            criteria: this._store.searchCriteria,
            channelNumbers: this._store.channelNumbers,
            programmeSchedule: this._store.schedule
        };

        try {
            await this._populate(request, updateIsPopulating);
        } catch (error) {
            console.error("Error", error);
            this._incrementRetries();
            await this._populate(request, updateIsPopulating);
        }
    }

    async updateGenre(genre: string, subGenre = "") {
        const searchCriteria = this._store.searchCriteria;
        searchCriteria.genre = genre;
        searchCriteria.subGenre = subGenre;

        const datesToFetch: Date[] = [this._store.dates[0]]; //Just get first date; //this._store.scheduleDates,

        const request: SearchRequest = {
            dates: datesToFetch,
            criteria: this._store.searchCriteria,
            channelNumbers: [],
            programmeSchedule: {}
        };

        this._store.updateSchedule({});
        this._store.updateScheduleDates([]);
        this._store.updateHasScheduleData(false);

        await this._populate(request);

        this._store.updateHasScheduleData(true);
    }

    async updatePackage($package: string) {
        const searchCriteria = this._store.searchCriteria;
        searchCriteria.package = $package;

        const datesToFetch: Date[] = [this._store.dates[0]]; //Just get first date; 

        const request: SearchRequest = {
            dates: datesToFetch,
            criteria: this._store.searchCriteria,
            channelNumbers: [],
            programmeSchedule: {}
        };

        this._store.updateSchedule({});
        this._store.updateScheduleDates([]);
        this._store.updateHasScheduleData(false);

        await this._populate(request);

        this._store.updateHasScheduleData(true);
    }

    async extendDays(endDay: number) {
        const startDay = 0;

        const dates: Date[] = [];

        const store = this._store;

        for (let i = startDay; i < endDay; i++) {
            const date = store.dates[i];

            if (store.scheduleDates.includes(date)) {
                continue;
            }

            dates.push(date);
        }

        if (!dates.length) {
            return;
        }

        await this.populateSchedule(dates);
    }
}

export default DataManager;